'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { Fragment, forwardRef, useId } from 'react'
import { TouchTarget } from './buttons/button'
import { Link, CustomLinkProps } from './link'
import { colors, semanticColors } from '../theme'

// Custom color palette for sidebar
const sidebarColors = {
  primary: "#2A333C",
  primaryLight: "#677D94",
  primaryDark: "#1A2128",
  secondary: "#F5B957",
  secondaryLight: "#FCECD1",
  secondaryDark: "#AB6D0A",
  conPrimary: "#1F2937",
  iconSecondary: "#7A6646",
  iconTertiary: "#94A3B8",
  iconFourth: "#CBD5E1",
  iconInverted: "#FFFFFF",
  badgePrimary: "#FF6B6B",
  green100: "#E0EDE2",
  green600: "#3A7D44",
  green800: "#2E6B3A",
  blue100: "#E0E8ED",
  blue500: "#4A6FA5",
  blue700: "#3A5F7D",
  blue800: "#2A4A6C",
  blue900: "#1A334C",
  blue600: "#3A5F7D",
  yellow100: "#FFF8E6",
  yellow800: "#D4A123",
  red100: "#FFEAEA",
  red500: "#C84648",
  red700: "#A23537",
  red900: "#7A2527",
  orange50: "#FFF5EB",
  orange100: "#FCE8D9",
  orange500: "#E67E34",
  orange700: "#C4450D",
  orange800: "#BF5A1F",
  card1: "#F8FAFC",
}

export function Sidebar({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) {
  return (
    <nav 
      {...props} 
      className={clsx(className, 'flex h-full min-h-0 flex-col')} 
      style={{
        background: `linear-gradient(180deg, ${sidebarColors.primary} 0%, ${sidebarColors.primaryDark} 100%)`,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      }}
    />
  )
}

export function SidebarHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col p-4 sm:p-5 lg:p-4',
        '[&>[data-slot=section]+[data-slot=section]]:mt-2 sm:[&>[data-slot=section]+[data-slot=section]]:mt-3 lg:[&>[data-slot=section]+[data-slot=section]]:mt-2'
      )}
      style={{ 
        borderBottom: `1px solid ${sidebarColors.primaryLight}20`,
        background: 'transparent',
        paddingBottom: '20px',
        paddingTop: '20px',
      }}
    />
  )
}

export function SidebarBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 sm:p-5 lg:p-4',
        '[&>[data-slot=section]+[data-slot=section]]:mt-6 sm:[&>[data-slot=section]+[data-slot=section]]:mt-8 lg:[&>[data-slot=section]+[data-slot=section]]:mt-6'
      )}
      style={{ 
        background: 'transparent',
      }}
    />
  )
}

export function SidebarFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col p-4 sm:p-5 lg:p-4',
        '[&>[data-slot=section]+[data-slot=section]]:mt-2 sm:[&>[data-slot=section]+[data-slot=section]]:mt-3 lg:[&>[data-slot=section]+[data-slot=section]]:mt-2'
      )}
      style={{ 
        background: 'transparent',
        paddingTop: '20px',
        paddingBottom: '20px',
        borderTop: `1px solid ${sidebarColors.primaryLight}20`,
      }}
    />
  )
}

export function SidebarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const id = useId()

  return (
    <LayoutGroup id={id}>
      <div 
        {...props} 
        data-slot="section" 
        className={clsx(
          className, 
          'flex flex-col gap-2 px-2 py-3',
          'sm:gap-3 sm:px-3 sm:py-4',
          'lg:gap-2 lg:px-2 lg:py-3'
        )} 
      />
    </LayoutGroup>
  )
}

export function SidebarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'hr'>) {
  return (
    <hr 
      {...props} 
      className={clsx(className, 'my-4 lg:-mx-4')} 
      style={{ 
        borderColor: `${sidebarColors.primaryLight}20`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '1px',
      }}
    />
  )
}

export function SidebarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-4 flex-1 sm:mt-8 lg:mt-4')} />
}

export function SidebarHeading({ className, ...props }: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3 
      {...props} 
      className={clsx(
        className, 
        'mb-3 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-left',
        'sm:mb-4 sm:px-4 sm:text-base lg:mb-3 lg:px-3 lg:text-sm'
      )} 
      style={{ 
        color: sidebarColors.iconTertiary,
        borderBottom: `1px solid ${sidebarColors.primaryLight}20`,
        background: `linear-gradient(90deg, ${sidebarColors.primaryLight}10 0%, transparent 100%)`,
        borderRadius: '6px',
        marginBottom: '16px',
        marginTop: '8px'
      }}
    />
  )
}

type SidebarItemLinkProps = Omit<CustomLinkProps, 'className' | 'type'>;
type SidebarItemButtonProps = Omit<Headless.ButtonProps, 'className'>;

export const SidebarItem = forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    ...restItemProps
  }: {
    current?: boolean;
    className?: string;
    children: React.ReactNode;
  } & (SidebarItemLinkProps | SidebarItemButtonProps),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = clsx(
    'flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-all duration-300',
    'sm:gap-3 sm:px-4 sm:py-3.5 sm:text-sm lg:gap-3 lg:px-3 lg:py-3 lg:text-sm',
    'data-[slot=icon]:*:size-5 data-[slot=icon]:*:shrink-0 sm:data-[slot=icon]:*:size-5 lg:data-[slot=icon]:*:size-5',
    'data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-4 sm:data-[slot=icon]:last:*:size-4 lg:data-[slot=icon]:last:*:size-4',
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-6 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6 lg:data-[slot=avatar]:*:size-6',
    'data-[hover]:shadow-md data-[active]:shadow-md',
    'data-[slot=icon]:*:data-[hover]:scale-110 data-[slot=icon]:*:data-[active]:scale-110',
    'data-[slot=icon]:*:data-[current]:scale-110',
    'hover:transform hover:scale-[1.02] active:scale-[0.98]',
    'min-h-[44px] sm:min-h-[48px] lg:min-h-[44px]',
  );

  const isLink = 'to' in restItemProps && (
        typeof (restItemProps as SidebarItemLinkProps).to === 'string' ||
        typeof (restItemProps as SidebarItemLinkProps).to === 'object'
    );

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-y-1 -left-1 w-1.5 rounded-full"
          style={{ 
            backgroundColor: sidebarColors.secondary,
            boxShadow: `0 0 8px ${sidebarColors.secondary}60`,
          }}
        />
      )}
      {isLink ? (
        <Headless.CloseButton as={Fragment} ref={ref as React.ForwardedRef<any>}>
          <Link
            {...(restItemProps as SidebarItemLinkProps)}
            className={classes}
            data-current={current ? 'true' : undefined}
            style={{
              color: current ? sidebarColors.yellow800 : sidebarColors.iconInverted,
              backgroundColor: current 
                ? sidebarColors.yellow100 
                : 'transparent',
              border: current 
                ? `1px solid ${sidebarColors.yellow800}40` 
                : '1px solid transparent',
              borderRadius: '8px',
              boxShadow: current 
                ? `0 2px 8px rgba(212, 161, 35, 0.15)` 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <TouchTarget>{children}</TouchTarget>
          </Link>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...(restItemProps as SidebarItemButtonProps)}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref as React.ForwardedRef<HTMLButtonElement>}
          style={{
            color: current ? sidebarColors.yellow800 : sidebarColors.iconInverted,
            backgroundColor: current 
              ? sidebarColors.yellow100 
              : 'transparent',
            border: current 
              ? `1px solid ${sidebarColors.yellow800}40` 
              : '1px solid transparent',
            borderRadius: '8px',
            boxShadow: current 
              ? `0 2px 8px rgba(212, 161, 35, 0.15)` 
              : '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

export function SidebarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span 
      {...props} 
      className={clsx(
        className, 
        'truncate font-medium text-sm leading-5',
        'sm:text-sm lg:text-sm'
      )} 
    />
  )
}